/**
 * Blishka Multi-Page Router with Strict Hierarchical Back Navigation
 *
 * Navigation Hierarchy:
 * Home (/)
 *   ├── Services (/services)
 *   │     └── Category Detail (/services?category=:catId)
 *   ├── Bundles (/bundles)
 *   │     └── Package Detail (/bundles?package=:pkgId)
 *   ├── Cart (/cart)
 *   ├── Chat (/chat)
 *   └── My Bookings (/my-bookings)
 *
 * Back-Button Rules:
 *   Category Detail -> Services (/services) -> Home (/)
 *   Package Detail  -> Bundles (/bundles)   -> Home (/)
 *   Cart / Chat / My Bookings -> Home (/)
 */

import { updateSEOMetadata } from './seo';

export type AppRoute =
  | 'home'
  | 'services'
  | 'bundles'
  | 'cart'
  | 'chat'
  | 'my-bookings'
  | 'admin'
  | 'super-admin'
  | 'staff'
  | 'partner';

export interface RouteInfo {
  route: AppRoute;
  path: string;
  category?: string | null;
  packageId?: string | null;
  depth?: number;
}

const ROUTE_PATH_MAP: Record<AppRoute, string> = {
  home: '/',
  services: '/services',
  bundles: '/bundles',
  cart: '/cart',
  chat: '/chat',
  'my-bookings': '/my-bookings',
  admin: '/admin',
  'super-admin': '/super-admin',
  staff: '/staff',
  partner: '/partner'
};


/**
 * Determine the route from the browser's current location (path, hash, and search).
 */
export function parseCurrentRoute(): RouteInfo {
  if (typeof window === 'undefined') {
    return { route: 'home', path: '/' };
  }

  const pathname = (window.location.pathname || '/').toLowerCase();
  const hash = (window.location.hash || '').toLowerCase();
  const search = (window.location.search || '').toLowerCase();
  const urlParams = new URLSearchParams(window.location.search);

  // Clean strings for exact & prefix matching
  const cleanPath = pathname.replace(/^\/+/, '').replace(/\/+$/, '');
  const cleanHash = hash.replace(/^#\/?/, '').split('?')[0].replace(/\/+$/, '');
  const portalParam = (
    urlParams.get('portal') ||
    urlParams.get('role') ||
    urlParams.get('console') ||
    urlParams.get('view') ||
    urlParams.get('page') ||
    ''
  ).toLowerCase().trim();
  const appParam = (urlParams.get('app') || urlParams.get('mode') || '').toLowerCase().trim();

  // Standalone app detection (e.g. launched from iPhone/Android home screen icon)
  const isStandalone =
    typeof window !== 'undefined' &&
    (window.matchMedia('(display-mode: standalone)').matches ||
      (window.navigator as unknown as { standalone?: boolean }).standalone === true);

  const savedPortal = typeof window !== 'undefined' ? localStorage.getItem('blishka_installed_portal') : null;
  const isRootLanding = cleanPath === '' || cleanPath === 'index.html';

  const matchSlug = (slug: string) => {
    return (
      cleanPath === slug ||
      cleanPath.startsWith(`${slug}/`) ||
      cleanHash === slug ||
      cleanHash.startsWith(`${slug}/`) ||
      portalParam === slug ||
      appParam === slug ||
      search === `?${slug}` ||
      search.includes(`${slug}=true`) ||
      search.includes(`${slug}=1`) ||
      search.startsWith(`?${slug}&`)
    );
  };

  // 1. Super Admin (matches explicit URL, app=admin param, or standalone recovery)
  if (
    matchSlug('super-admin') ||
    matchSlug('superadmin') ||
    matchSlug('owner') ||
    matchSlug('finance') ||
    appParam === 'admin' ||
    (isStandalone && isRootLanding && savedPortal === 'super-admin')
  ) {
    return { route: 'super-admin', path: '/super-admin' };
  }

  // 2. Regular Admin
  if (
    matchSlug('admin') ||
    matchSlug('admin-portal') ||
    (isStandalone && isRootLanding && savedPortal === 'admin')
  ) {
    return { route: 'admin', path: '/admin' };
  }

  // 3. Staff / Beautician Partner (matches explicit URL, app=staff param, or standalone recovery)
  if (
    matchSlug('staff') ||
    matchSlug('partner') ||
    matchSlug('staff-portal') ||
    matchSlug('partner-portal') ||
    matchSlug('beautician') ||
    appParam === 'staff' ||
    (isStandalone && isRootLanding && (savedPortal === 'staff' || savedPortal === 'partner'))
  ) {
    return { route: 'staff', path: '/staff' };
  }

  // 4. My Bookings
  if (matchSlug('my-bookings') || matchSlug('bookings') || matchSlug('mybookings')) {
    return { route: 'my-bookings', path: '/my-bookings' };
  }

  // 5. Cart / Booking
  if (matchSlug('cart') || matchSlug('checkout') || matchSlug('book') || matchSlug('booking')) {
    return { route: 'cart', path: '/cart' };
  }

  // 6. Chat
  if (matchSlug('chat') || matchSlug('assistant') || matchSlug('chatbot') || matchSlug('help')) {
    return { route: 'chat', path: '/chat' };
  }

  // 7. Bundles
  if (matchSlug('bundles') || matchSlug('bundle') || matchSlug('packages')) {
    const packageId = urlParams.get('package') || urlParams.get('bundle') || urlParams.get('pkg') || null;
    return { route: 'bundles', path: '/bundles', packageId };
  }

  // 8. Services Menu
  if (matchSlug('services') || matchSlug('menu') || matchSlug('service-menu') || matchSlug('catalog')) {
    const category = urlParams.get('category') || urlParams.get('cat') || null;
    return { route: 'services', path: '/services', category };
  }

  return { route: 'home', path: '/' };
}

// Custom event name for route updates
export const ROUTE_CHANGE_EVENT = 'blishka-route-changed';

/**
 * Determine depth in the navigation hierarchy:
 * - 0: Home (/)
 * - 1: Top-level sibling extensions (/services, /bundles, /cart, /chat, /my-bookings, /admin, /staff)
 * - 2: Sub-pages (/services?category=..., /bundles?package=...)
 */
export function getRouteDepth(
  route: AppRoute,
  category?: string | null,
  packageId?: string | null
): number {
  if (route === 'home') return 0;
  if (route === 'services' && category) return 2;
  if (route === 'bundles' && packageId) return 2;
  return 1;
}

let lastActiveRoute: RouteInfo = { route: 'home', path: '/' };

function dispatchRouteChange(
  route: AppRoute,
  path: string,
  category?: string | null,
  packageId?: string | null
) {
  window.dispatchEvent(
    new CustomEvent(ROUTE_CHANGE_EVENT, {
      detail: {
        route,
        path,
        category: category || null,
        packageId: packageId || null
      }
    })
  );
}

/**
 * Determine the parent destination for hierarchical back navigation:
 * - Category View (/services?category=...) -> Services All Categories (/services)
 * - Services (/services)                   -> Home (/)
 * - Package Detail (/bundles?package=...)  -> Bundles All Packages (/bundles)
 * - Bundles (/bundles)                     -> Home (/)
 * - Cart / Chat / My Bookings              -> Home (/)
 */
export function getHierarchicalParent(current?: RouteInfo): {
  route: AppRoute;
  path: string;
  category?: string | null;
  packageId?: string | null;
} {
  const info = current || parseCurrentRoute();

  if (info.route === 'services') {
    if (info.category) {
      return { route: 'services', path: '/services', category: null };
    }
    return { route: 'home', path: '/' };
  }

  if (info.route === 'bundles') {
    if (info.packageId) {
      return { route: 'bundles', path: '/bundles', packageId: null };
    }
    return { route: 'home', path: '/' };
  }

  // All other subpages (cart, chat, my-bookings, admin, staff) lead back to Home
  return { route: 'home', path: '/' };
}

/**
 * Navigate to a specific route or path.
 *
 * Back-Button Contract:
 * - Top-level pages (/services, /bundles, /cart, /chat) are peer extensions.
 *   Navigating between them replaces the current level-1 entry in history.
 *   Therefore, pressing the browser Back button from ANY top-level page
 *   ALWAYS navigates directly to Home (/), never looping through previously clicked extensions.
 * - Subpages (/services?category=..., /bundles?package=...) push a new history entry.
 *   Pressing Back from Category -> Services -> Home.
 *   Pressing Back from Package -> Bundles -> Home.
 */
export function navigateTo(
  target: AppRoute | string,
  options?: {
    replace?: boolean;
    category?: string | null;
    packageId?: string | null;
    scrollToTop?: boolean;
    skipHistory?: boolean;
  }
) {
  let targetRoute: AppRoute = 'home';
  let targetPath = '/';

  if (target in ROUTE_PATH_MAP) {
    targetRoute = target as AppRoute;
    targetPath = ROUTE_PATH_MAP[targetRoute];
  } else {
    // Target is a path string like '/services' or '/bundles'
    const cleanPath = (target as string).toLowerCase().split('?')[0].split('#')[0];
    if (cleanPath === '/services' || cleanPath === 'services') targetRoute = 'services';
    else if (cleanPath === '/bundles' || cleanPath === 'bundles') targetRoute = 'bundles';
    else if (cleanPath === '/cart' || cleanPath === 'cart') targetRoute = 'cart';
    else if (cleanPath === '/chat' || cleanPath === 'chat') targetRoute = 'chat';
    else if (cleanPath === '/my-bookings' || cleanPath === 'my-bookings') targetRoute = 'my-bookings';
    else if (cleanPath === '/admin' || cleanPath === 'admin') targetRoute = 'admin';
    else if (cleanPath === '/super-admin' || cleanPath === 'super-admin' || cleanPath === '/superadmin' || cleanPath === 'superadmin') targetRoute = 'super-admin';
    else if (cleanPath === '/staff' || cleanPath === 'staff' || cleanPath === '/partner' || cleanPath === 'partner') targetRoute = 'staff';
    else targetRoute = 'home';

    targetPath = ROUTE_PATH_MAP[targetRoute];
  }

  if (targetRoute === 'services' && options?.category) {
    targetPath += `?category=${encodeURIComponent(options.category)}`;
  } else if (targetRoute === 'bundles' && options?.packageId) {
    targetPath += `?package=${encodeURIComponent(options.packageId)}`;
  }

  const current = parseCurrentRoute();
  const currentDepth = getRouteDepth(current.route, current.category, current.packageId);
  const targetDepth = getRouteDepth(targetRoute, options?.category, options?.packageId);

  const currentPathWithSearch = window.location.pathname + window.location.search;
  const isSamePath = currentPathWithSearch === targetPath && !window.location.hash;

  if (!options?.skipHistory && !isSamePath) {
    const stateObj = {
      blishkaApp: true,
      route: targetRoute,
      path: targetPath,
      category: options?.category || null,
      packageId: options?.packageId || null,
      depth: targetDepth
    };

    // Case 1: Returning to Home (depth 0) from an extension
    if (targetDepth === 0) {
      if (currentDepth === 1 && window.history.state?.blishkaApp) {
        window.history.back();
        lastActiveRoute = { route: 'home', path: '/', depth: 0 };
        updateSEOMetadata('home');
        if (options?.scrollToTop !== false) window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
        dispatchRouteChange('home', '/', null, null);
        return;
      } else if (currentDepth === 2 && window.history.state?.blishkaApp) {
        window.history.go(-2);
        lastActiveRoute = { route: 'home', path: '/', depth: 0 };
        updateSEOMetadata('home');
        if (options?.scrollToTop !== false) window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
        dispatchRouteChange('home', '/', null, null);
        return;
      } else {
        window.history.replaceState(stateObj, '', targetPath);
      }
    }
    // Case 2: Switching between Level 1 sibling extensions (services <-> bundles <-> cart <-> chat)
    // ALWAYS use replaceState so they do NOT create a chained history stack!
    else if (currentDepth === 1 && targetDepth === 1) {
      window.history.replaceState(stateObj, '', targetPath);
    }
    // Case 3: Stepping back from Level 2 to its direct Level 1 parent
    // e.g. /services?category=waxing -> /services
    else if (currentDepth === 2 && targetDepth === 1 && current.route === targetRoute) {
      window.history.back();
      lastActiveRoute = { route: targetRoute, path: targetPath, depth: 1 };
      updateSEOMetadata(targetRoute, null, null);
      if (options?.scrollToTop !== false) window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
      dispatchRouteChange(targetRoute, targetPath, null, null);
      return;
    }
    // Case 4: Navigating from Level 2 to a different Level 1 extension
    // e.g. /services?category=waxing -> /bundles or /cart
    // Unwind Level 2 so the stack becomes [ Home, targetExtension ]
    else if (currentDepth === 2 && targetDepth === 1 && current.route !== targetRoute) {
      sessionStorage.setItem(
        'blishka_unwind_to',
        JSON.stringify({ route: targetRoute, path: targetPath, depth: 1 })
      );
      window.history.go(-1);
      return;
    }
    // Case 5: Switching between sibling Level 2 pages (category -> another category)
    else if (currentDepth === 2 && targetDepth === 2) {
      window.history.replaceState(stateObj, '', targetPath);
    }
    // Case 6: Drilling down from Home (0) -> Level 1 (1), or Level 1 (1) -> Level 2 (2)
    else {
      if (options?.replace) {
        window.history.replaceState(stateObj, '', targetPath);
      } else {
        window.history.pushState(stateObj, '', targetPath);
      }
    }
  }

  lastActiveRoute = {
    route: targetRoute,
    path: targetPath,
    category: options?.category || null,
    packageId: options?.packageId || null,
    depth: targetDepth
  };

  // Update Document Title & SEO Metadata (meta description, canonical, OG, Twitter)
  updateSEOMetadata(targetRoute, options?.category, options?.packageId);

  // Scroll to top if requested
  if (options?.scrollToTop !== false) {
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
    requestAnimationFrame(() => {
      window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
    });
    setTimeout(() => {
      window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
    }, 50);
  }

  // Dispatch custom route change event
  dispatchRouteChange(targetRoute, targetPath, options?.category, options?.packageId);
}

/**
 * Handle PopState events with hierarchical guard:
 * - If user was on a Level-1 extension and pops to another Level-1 extension,
 *   directly redirect to Home (/) as requested by user.
 * - Handles unwinding from Level-2 to a new Level-1 extension via sessionStorage.
 */
export function handlePopStateGuard(): RouteInfo {
  // Check for pending Level-2 unwind
  const pendingRaw = sessionStorage.getItem('blishka_unwind_to');
  if (pendingRaw) {
    sessionStorage.removeItem('blishka_unwind_to');
    try {
      const pending = JSON.parse(pendingRaw);
      window.history.replaceState(
        {
          blishkaApp: true,
          route: pending.route,
          path: pending.path,
          depth: pending.depth
        },
        '',
        pending.path
      );
      lastActiveRoute = {
        route: pending.route,
        path: pending.path,
        depth: pending.depth
      };
      updateSEOMetadata(pending.route as AppRoute, null, null);
      return lastActiveRoute;
    } catch {
      // ignore
    }
  }

  const current = parseCurrentRoute();
  const prevDepth = getRouteDepth(lastActiveRoute.route, lastActiveRoute.category, lastActiveRoute.packageId);
  const currentDepth = getRouteDepth(current.route, current.category, current.packageId);

  // If previous was a Level 1 extension and popped to another Level 1 extension,
  // enforce direct-to-home rule:
  if (prevDepth === 1 && currentDepth === 1 && lastActiveRoute.route !== current.route) {
    window.history.replaceState({ blishkaApp: true, route: 'home', path: '/', depth: 0 }, '', '/');
    const homeInfo: RouteInfo = { route: 'home', path: '/', depth: 0 };
    lastActiveRoute = homeInfo;
    updateSEOMetadata('home');
    return homeInfo;
  }

  lastActiveRoute = {
    ...current,
    depth: currentDepth
  };

  updateSEOMetadata(current.route, current.category, current.packageId);

  return current;
}

/**
 * Execute hierarchical back navigation.
 * Resolves the deterministic parent according to the hierarchy and navigates there.
 */
export function handleHierarchicalBack() {
  const current = parseCurrentRoute();
  const parent = getHierarchicalParent(current);
  navigateTo(parent.route, {
    category: parent.category,
    packageId: parent.packageId
  });
}

/**
 * Return to Home page.
 */
export function goHome() {
  navigateTo('home');
}

/**
 * Initialize history base state to ensure pressing Back on direct subpage landings
 * always leads back to the parent and Home (/) rather than closing the tab or clearing the site.
 */
export function initHistoryGuard(): RouteInfo {
  if (typeof window === 'undefined') {
    return { route: 'home', path: '/' };
  }

  const initial = parseCurrentRoute();
  // Preserve the exact full destination URL that the user entered BEFORE touching history
  const currentTargetUrl = window.location.pathname + window.location.search + window.location.hash;

  if (initial.route === 'services' && initial.category) {
    // Direct landing on Category Detail: Seed Home (0) -> Services (1) -> Category Detail (2)
    if (!window.history.state || !window.history.state.blishkaApp) {
      window.history.replaceState({ blishkaApp: true, route: 'home', path: '/', depth: 0 }, '', '/');
      window.history.pushState({ blishkaApp: true, route: 'services', path: '/services', depth: 1 }, '', '/services');
      window.history.pushState(
        {
          blishkaApp: true,
          route: 'services',
          path: currentTargetUrl,
          category: initial.category,
          depth: 2
        },
        '',
        currentTargetUrl
      );
    }
  } else if (initial.route === 'bundles' && initial.packageId) {
    // Direct landing on Package Detail: Seed Home (0) -> Bundles (1) -> Package Detail (2)
    if (!window.history.state || !window.history.state.blishkaApp) {
      window.history.replaceState({ blishkaApp: true, route: 'home', path: '/', depth: 0 }, '', '/');
      window.history.pushState({ blishkaApp: true, route: 'bundles', path: '/bundles', depth: 1 }, '', '/bundles');
      window.history.pushState(
        {
          blishkaApp: true,
          route: 'bundles',
          path: currentTargetUrl,
          packageId: initial.packageId,
          depth: 2
        },
        '',
        currentTargetUrl
      );
    }
  } else if (initial.route !== 'home') {
    // Direct landing on /staff, /admin, /super-admin, /cart, /chat, etc.: Seed Home (0) -> Subpage (1)
    if (!window.history.state || !window.history.state.blishkaApp) {
      window.history.replaceState({ blishkaApp: true, route: 'home', path: '/', depth: 0 }, '', '/');
      window.history.pushState(
        {
          blishkaApp: true,
          route: initial.route,
          path: currentTargetUrl,
          depth: 1
        },
        '',
        currentTargetUrl
      );
    }
  } else {
    if (!window.history.state || !window.history.state.blishkaApp) {
      window.history.replaceState({ blishkaApp: true, route: 'home', path: '/', depth: 0 }, '', '/');
    }
  }

  // Set initial Document Title and SEO Metadata
  updateSEOMetadata(initial.route, initial.category, initial.packageId);

  lastActiveRoute = {
    ...initial,
    depth: getRouteDepth(initial.route, initial.category, initial.packageId)
  };

  return initial;
}

